import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Address } from '../../../../api/app-types/address.app';
import { ContactPoint, ContactPointCategory } from '../../../../api/app-types/contact-point.app';
import { Gender } from '../../../../api/app-types/gender.app';
import { PersonType } from '../../../../api/app-types/person-type.app';
import {
	CATEGORY_RELATIVE_INITIALS,
	ClarificationValue,
	CreateContactPoint,
	Relative,
	UpdateRelative,
} from '../../../../api/app-types/relative.app';
import { CLIENT_API } from '../../../../api/injection-tokens/services.token';
import { AddressCompletion } from '../../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../../core/components/contact-point/contact-point';
import { LvrDataProtection } from '../../../../core/components/data-protection/data-protection';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { isContactPointComplete } from '../../../../shared/helpers/is-contact-point-complete.func';
import { wrapPromiseWithNotify } from '../../../../shared/helpers/notify.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';

@Component({
	selector: 'lvr-client-relatives-edit',
	templateUrl: './client-relatives-edit.html',
	imports: [
		ReactiveFormsModule,
		LvrField,
		MatFormFieldModule,
		LvrInputDirective,
		TranslocoPipe,
		LvrPaddingDirective,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrAppendAsteriskPipe,
		MatSelectModule,
		MatInput,
		MatTooltip,
		MatIcon,
		MatIconButton,
		LvrContactPoint,
		MatCheckbox,
		AddressCompletion,
		LvrDataProtection,
		LvrDefaultActions,
		LvrButton,
		LvrSearchableSelect,
	],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
})
export class ClientRelativesEdit {
	private readonly fb = inject(FormBuilder);
	private readonly persistentStorage = inject(FormPersistenceService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly api = inject(CLIENT_API);
	private readonly router = inject(Router);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	private readonly data = toSignal(inject(ActivatedRoute).data);
	private readonly clientId = computed(() => this.data()?.['clientId'] as number);
	public readonly genders = computed(() => this.data()?.['genders'] as Gender[]);
	public readonly contactPointCategories = computed(
		() => (this.data()?.['contactPointCategories'].entities || []) as ContactPointCategory[],
	);
	private readonly personTypeResp = computed(
		() => this.data()?.['categoryPersonTypes'] as PaginatedResponse<PersonType>,
	);
	private readonly relative = computed(() => this.data()?.['relative'] as Relative);
	public readonly personTypes = computed(() => this.personTypeResp().entities || []);
	public readonly personTypeLabel = (personType: PersonType): string => personType.name;
	public readonly environment = inject(ENVIRONMENT);
	private readonly nameRegex = new RegExp(this.environment.nameRegexStr);
	private readonly titleRegex = new RegExp(this.environment.titleRegexStr);

	public readonly saving = signal(false);

	public readonly draftId = computed(() => this.relative().id);
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly canEditClientEnvironmentsPermission = hasPermission(
		this.session.permissions,
		'ClientEnvironments.Edit',
	);
	public readonly form = this.fb.group({
		personCategoryId: this.fb.control<number | undefined>(this.relative().categoryPersonCategory.id, {
			validators: [Validators.required],
		}),
		remark: this.fb.control<string>(this.relative().remark ?? ''),
		title: this.fb.control(this.relative().title ?? '', Validators.pattern(this.titleRegex)),
		gender: this.fb.control<number | undefined>(this.relative().genderId),
		name: this.fb.control(this.relative().lastName, [Validators.required, Validators.pattern(this.nameRegex)]),
		firstName: this.fb.control(this.relative().firstName, [Validators.pattern(this.nameRegex)]),
		communicationWays: this.fb.control<ContactPoint[]>(this.relative().contactPoints ?? [], {
			validators: [Validators.max(5)],
		}),
		isSupervised: this.fb.control<boolean>(this.relative().isSupervisor, {}),
		otherText: this.fb.control<string>(this.relative().otherText ?? '', {}),
		address: this.fb.control<Address | undefined>(this.relative().address),
		clarification: this.fb.control<ClarificationValue>({
			hasBeenClarified: false,
			clarifiedAt: undefined,
			typeOfClarification: undefined,
		}),
	});

	private readonly personTypeIdCtrl = this.form.controls.personCategoryId;
	private readonly remarkCtrl = this.form.controls.remark;
	private readonly personTypeIdValue = toSignal(this.personTypeIdCtrl.valueChanges, {
		initialValue: this.personTypeIdCtrl.value,
	});
	public readonly selectedPersonType = computed(() => {
		const selectedId = this.personTypeIdValue();
		return this.personTypes().find((item) => item.id === selectedId);
	});
	public readonly shouldDisplayRemarkField = computed(() => {
		return this.selectedPersonType()?.initials === CATEGORY_RELATIVE_INITIALS.TRUSTED_PERSON_OTHER;
	});
	public constructor() {
		this.handleRemarkField();
	}

	private handleRemarkField() {
		effect(() => {
			const needsRemark = this.shouldDisplayRemarkField();

			if (needsRemark) {
				this.remarkCtrl.addValidators([Validators.maxLength(50)]);
			} else {
				this.remarkCtrl.clearValidators();
				if (this.remarkCtrl.value) this.remarkCtrl.setValue('', { emitEvent: false });
			}
			this.remarkCtrl.updateValueAndValidity({ emitEvent: false });
		});

		effect(() => {
			const r = this.relative();
			if (!r) return;

			const clar: ClarificationValue = {
				hasBeenClarified: r.hasBeenClarified ?? false,
				clarifiedAt: r.clarifiedAt ?? undefined,
				typeOfClarification: r.typeOfClarification ?? undefined,
			};

			this.form.controls.clarification.setValue(clar, { emitEvent: false });
		});
	}

	public get communicationWays(): ContactPoint[] {
		return (this.form.get('communicationWays')?.value ?? []) as ContactPoint[];
	}

	public addCommunicationWay() {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const cp: ContactPoint = {
			id: -1,
			preferredWayOfContact: false,
			categoryContactPointId: -1,
			contactValue: '',
			remark: '',
			personId: -1,
			personTypeId: -1,
		};
		values.push(cp);
		this.form.get('communicationWays')?.patchValue(values);
	}

	public removeCommunicationWay(index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index !== -1) {
			values.splice(index, 1);
			this.form.get('communicationWays')?.patchValue(values);
		}
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];

		if (index >= 0 && index < values.length) {
			Object.assign(values[index], data.value); // Update same item in array
		} else {
			values.push(data.value); // Add if new
		}
	}

	public cancel() {
		this.persistentStorage.deleteDraft('relative', this.draftId());
		this.router.navigate(['/desk/client', this.clientId(), 'relatives', this.relative().id]);
	}

	public async save() {
		if (this.saving()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const EMPTY_ADDRESS = {
			streetAndHouseNumber: '',
			postalCodeAndCity: '',
		};

		this.saving.set(true);

		try {
			const value = this.form.value;

			const clar = value.clarification ?? {
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			};

			const body: UpdateRelative = {
				lastName: value.name?.trim() ?? '',
				address: value.address === null ? (EMPTY_ADDRESS as Address) : value.address,
				contactPoints: [],
				firstName: value.firstName?.trim() ?? '',
				isSupervisor: value.isSupervised ?? false,
				categoryPersonCategoryId: value.personCategoryId!,
				remark: value.remark?.trim() ?? '',
				title: value.title?.trim() ?? '',
				genderId: value.gender ?? undefined,
				otherText: value.otherText?.trim() ?? '',
				hasBeenClarified: clar.hasBeenClarified,
				clarifiedAt: clar.clarifiedAt,
				typeOfClarification: clar.typeOfClarification,
			};

			const filteredCws = (value.communicationWays ?? []).filter(isContactPointComplete);

			if (filteredCws.length > 0) {
				const additionalCws: CreateContactPoint[] = filteredCws.map((cp) => ({
					id: cp.id === -1 ? undefined : cp.id,
					preferredWayOfContact: cp.preferredWayOfContact,
					categoryContactPointId: cp.categoryContactPointId,
					contactValue: cp.contactValue?.trim(),
					remark: cp.remark?.trim(),
				}));

				body.contactPoints!.push(...additionalCws);
			}

			await wrapPromiseWithNotify(
				this.api.updateRelative(this.clientId(), this.relative().id, body),
				this.transloco.translate('base.notification_message.relatives_edited'),
				this.ntfy,
				this.transloco.translate('base.notification_message.relatives_edit_error'),
			);
			this.persistentStorage.deleteDraft('relative', this.draftId());
			await this.router.navigate(['/desk/client', this.clientId(), 'relatives', this.relative().id]);
		} finally {
			this.saving.set(false);
		}
	}

	/**
	 * Returns 'true' once the data protection clarification value was already given.
	 */
	public readonly isClarificationFinal = computed(() => {
		const r = this.relative();
		return !!(r?.hasBeenClarified && r?.clarifiedAt && r?.typeOfClarification != undefined);
	});
}
